import { Directive, inject } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FcGenericItemType, FcGenericListItemDto, FcModifyGenericItemType, FormGroupValidationRules, SystemGuid } from 'webcommon/shared';
import { FindControlRepository } from 'webcommon/webapi';
import { FcItemModalMode } from '../../models';
import { FcListValidationService } from './fc-list-validation';
import { FcItemModalBaseComponent } from './fc-item-modal-base.component';
import { FindControlIdService } from 'webcommon/legacy-common';
import { HttpErrorResponse } from '@angular/common/http';
import { reportErrorToUserAndServer } from 'webcommon/core-ui';

@Directive()
export abstract class FcGenericItemModalBaseComponent extends FcItemModalBaseComponent {
  // Services
  public readonly findControlRepository = inject(FindControlRepository);
  public readonly fcListValidation = inject(FcListValidationService);
  public readonly systemGuid = inject(SystemGuid);
  public readonly findControlIdService = inject(FindControlIdService);
  public readonly FindControlIdService = FindControlIdService;

  abstract listItem: FcGenericListItemDto;
  abstract form: FormGroup;

  public baseId = 'fc-item';

  public isSubmitted = false;
  public isDataLoaded = false;

  abstract setForm(): void;

  public loadListItem<T = FcGenericItemType>(defaultValidationRules: FormGroupValidationRules = {}) {
    this.fcListValidation.initValidation(this.fcModalItemContext, defaultValidationRules, this.listItem);
    this.getItem<T>(defaultValidationRules);
  }

  private getItem<T = FcGenericItemType>(defaultValidationRules: FormGroupValidationRules = {}): void {
    if (this.fcModalItemContext.mode === FcItemModalMode.Modify || this.fcModalItemContext.mode === FcItemModalMode.NewRelItem) {
      this.isLoading = true;
      this.findControlRepository
        .getItem<T>(this.fcModalItemContext.findControlId, this.fcModalItemContext.selectedFindItemPk)
        .pipe(
          reportErrorToUserAndServer(this.apNotificationService),
          this.takeUntilDestroy())
        .subscribe({
          next: (data: T) => {
            this.listItem = {
              ...this.listItem,
              ...data,
            };
            this.isDataLoaded = true;
            this.fcListValidation.initValidation(this.fcModalItemContext, defaultValidationRules, this.listItem);
            this.setForm();
          },
          complete: () => {
            this.isLoading = false;
          },
        });
    } else {
      this.fcListValidation.initValidation(this.fcModalItemContext, defaultValidationRules, this.listItem);
      this.setForm();
    }
  }

  public save<T = FcModifyGenericItemType>(modifyItemDto: T): void {
    this.isSubmitted = true;
    this.alertService.clearAllAlerts();
    if (!this.form.valid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    if (this.fcModalItemContext.mode === FcItemModalMode.Modify) {
      this.findControlRepository
        .updateItem<T>(this.fcModalItemContext.findControlId, this.listItem.Id, modifyItemDto)
        .pipe(this.takeUntilDestroy())
        .subscribe({
          next: (data) => {
            this.onClose(data);
          },
          error: (errorResponse: unknown) => {
            let errorMessage = 'Your change could not be saved due to an unknown error.';
            if (errorResponse instanceof HttpErrorResponse){
              const result = errorResponse.error?.ModelState?.commandResult;
              if (result && Array.isArray(result) && result.length > 0 && result[0]) {
                errorMessage = result.join('\n');
              }
            }
            this.apNotificationService.error(errorMessage, 'Something went wrong.');
            this.isSaving = false;
          },
        });
    } else {
      this.findControlRepository
        .createItem<T>(this.fcModalItemContext.findControlId, modifyItemDto)
        .pipe(this.takeUntilDestroy())
        .subscribe({
          next: (data) => {
            this.onClose(data);
          },
          error: (errorResponse: unknown) => {
            let errorMessage = 'Your new item could not be saved due to an unknown error.';
            if (errorResponse instanceof HttpErrorResponse){
              const result = errorResponse.error?.ModelState?.commandResult;
              if (result && Array.isArray(result) && result.length > 0 && result[0]) {
                errorMessage = result.join('\n');
              }
            }
            this.apNotificationService.error(errorMessage, 'Something went wrong.');
            this.isSaving = false;
          },
        });
    }
  }

  public parseFindControlValue(value: string | string[], multiselect?: false): string;
  public parseFindControlValue(value: string | string[], multiselect?: true): string[];
  public parseFindControlValue(value: string | string[], multiselect: boolean = false): string | string[] {
    if (Array.isArray(value) && value.length > 0) {
      return multiselect ? value : value[0];
    } else if (typeof value === 'string' && value.length > 0) {
      return multiselect ? [value] : value;
    }
    return multiselect ? [] : this.systemGuid.GuidEmpty;
  }

  public getFindControlValue(value: string | string[]): string[] {
    const newSelectedPks: string[] = Array.isArray(value)
      ? [...value.filter(pk => typeof pk === 'string' && pk.length > 0)]
      : typeof value === 'string' && value.length > 0
      ? [value]
      : [];
    return newSelectedPks;
  }

  public setDateToControl(control: FormControl, date?: Date | string | number | null, allowEmpty: boolean = false) {
    if (date != null) {
      date = date instanceof Date ? date : new Date(date);
      if (date.toString() !== 'Invalid Date') {
        control.setValue(date);
      } else {
        control.setValue(allowEmpty ? null : new Date());
      }
    } else {
      control.setValue(allowEmpty ? null : new Date());
    }
  }

  public validateForm(group: FormGroup | FormArray = this.form): void {
    if (group instanceof FormGroup || group instanceof FormArray) {
      for (const abstractControl of Object.values(group.controls)) {
        abstractControl.updateValueAndValidity({ onlySelf: true });
        if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
          this.validateForm(abstractControl);
        }
      }
    }
  }

  public showErrors(control: AbstractControl, isSubmitted: boolean = false): boolean {
    return FcListValidationService.showErrors(control, isSubmitted);
  }

  public isRequired(control: FormControl): boolean {
    return control.hasValidator(Validators.required);
  }
}
